import React, { useState } from 'react';

const DebugApi: React.FC = () => {
  const [results, setResults] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://homework-guy-email-server.onrender.com/api';

  const appendResult = (text: string) => {
    setResults(prev => prev + '\n' + text);
  };

  const clearResults = () => {
    setResults('');
  };

  const testEndpoint = async (endpoint: string, method: string = 'GET', body: any = null) => {
    setLoading(true);
    try {
      appendResult(`Testing ${method} ${apiBaseUrl}${endpoint}...`);
      
      const options: RequestInit = {
        method,
        headers: {
          'Accept': 'application/json',
        },
      };
      
      if (body) {
        if (method !== 'GET') {
          options.headers = {
            ...options.headers,
            'Content-Type': 'application/json',
          };
          options.body = JSON.stringify(body);
        }
      }
      
      const response = await fetch(`${apiBaseUrl}${endpoint}`, options);
      
      appendResult(`Status: ${response.status} ${response.statusText}`);
      
      // Log response headers
      appendResult('Response Headers:');
      response.headers.forEach((value, key) => {
        appendResult(`  ${key}: ${value}`);
      });
      
      try {
        const text = await response.text();
        appendResult(`Response body: ${text}`);
        
        try {
          const json = JSON.parse(text);
          appendResult(`Parsed JSON: ${JSON.stringify(json, null, 2)}`);
        } catch (e) {
          appendResult('Not valid JSON');
        }
      } catch (e) {
        appendResult(`Error reading response: ${e}`);
      }
    } catch (error: any) {
      appendResult(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const testFormData = async () => {
    setLoading(true);
    try {
      appendResult(`Testing FormData POST to ${apiBaseUrl}/crypto-payment-with-attachments...`);
      
      const formData = new FormData();
      
      // Create a simple JSON object
      const jsonData = JSON.stringify({
        cryptoType: 'BTC',
        transactionId: '1234567890abcdef',
        formData: {
          basicInfo: {
            fullName: 'Test User',
            email: 'test@example.com',
            phone: '123-456-7890',
            preferredContact: 'email'
          },
          additionalRequirements: {
            specialInstructions: 'This is a test'
          }
        },
        calculatorData: {
          serviceType: 'essay',
          academicLevel: 'undergraduate',
          subjects: ['Computer Science']
        },
        totalPrice: 100
      });
      
      formData.append('data', jsonData);
      
      // Create a simple test file
      const testFile = new File(['Test file content'], 'test.txt', { type: 'text/plain' });
      formData.append('attachments', testFile);
      
      const response = await fetch(`${apiBaseUrl}/crypto-payment-with-attachments`, {
        method: 'POST',
        body: formData,
      });
      
      appendResult(`Status: ${response.status} ${response.statusText}`);
      
      // Log response headers
      appendResult('Response Headers:');
      response.headers.forEach((value, key) => {
        appendResult(`  ${key}: ${value}`);
      });
      
      try {
        const text = await response.text();
        appendResult(`Response body: ${text}`);
        
        try {
          const json = JSON.parse(text);
          appendResult(`Parsed JSON: ${JSON.stringify(json, null, 2)}`);
        } catch (e) {
          appendResult('Not valid JSON');
        }
      } catch (e) {
        appendResult(`Error reading response: ${e}`);
      }
    } catch (error: any) {
      appendResult(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>API Endpoint Tester</h1>
      <p>Use this page to test API connectivity and diagnose issues.</p>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => testEndpoint('/test')}
          disabled={loading}
          style={{ margin: '5px', padding: '8px 16px' }}
        >
          Test GET /api/test
        </button>
        
        <button 
          onClick={() => testEndpoint('/crypto-payment-test')}
          disabled={loading}
          style={{ margin: '5px', padding: '8px 16px' }}
        >
          Test GET /api/crypto-payment-test
        </button>
        
        <button 
          onClick={() => testFormData()}
          disabled={loading}
          style={{ margin: '5px', padding: '8px 16px' }}
        >
          Test FormData POST
        </button>
        
        <button 
          onClick={clearResults}
          style={{ margin: '5px', padding: '8px 16px' }}
        >
          Clear Results
        </button>
      </div>
      
      <div style={{ marginTop: '20px' }}>
        <h3>Results:</h3>
        <pre 
          style={{ 
            background: '#f5f5f5', 
            padding: '15px', 
            borderRadius: '5px',
            height: '400px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {results || 'No results yet. Click a button above to test an endpoint.'}
        </pre>
      </div>
    </div>
  );
};

export default DebugApi; 